import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import db, { collection, doc, query, where, addDoc, updateDoc, deleteDoc, serverTimestamp } from '../firebase/firestore';
import { subscribeWithRetry } from './useResilientSnapshot';

// A club's teams (U14, U16 Girls, First Team…) — one club-level list in
// academies/{academyId}/teams, managed from Settings → Teams (admins) or
// Academy Portal → Club settings (head coach). Leads, players and Academy
// Portal groups each carry an optional `teamId` pointing into it; renaming a
// team needs no backfill since nothing stores the name.
//
// Also resolves the signed-in staff member's own team scope — users/{uid}
// .teamIds, set by an admin/head coach via setTeamMemberTeams. Empty or
// missing means "all teams" (every account predating this feature). Like
// staffRole permissions this is a UI-level scope, not a firestore.rules
// boundary: it decides what each list shows, not what the account could
// read directly.
//
// Agency tenants (PLYRMATCH) never get teams — they recruit for other
// clubs rather than running squads of their own.
export function useTeams() {
  const { academyId, isAgencyTenant, isGuardian, userProfile, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  // Staff only — firestore.rules don't let a player/parent account read the
  // team list, and nothing they see is team-filtered anyway.
  const enabled = !!academyId && !isAgencyTenant && !isGuardian;

  useEffect(() => {
    if (!enabled) { setTeams([]); setLoading(false); return undefined; }
    return subscribeWithRetry(collection(db, 'academies', academyId, 'teams'), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }));
      setTeams(data);
      setLoading(false);
    }, { onGiveUp: () => setLoading(false) });
  }, [enabled, academyId]);

  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const hasTeams = teams.length > 0;

  const rawTeamIds = userProfile?.teamIds;
  const myTeamIds = useMemo(
    () => (hasTeams && Array.isArray(rawTeamIds) && rawTeamIds.length > 0 ? new Set(rawTeamIds) : null),
    [hasTeams, rawTeamIds],
  );
  const isScoped = !!myTeamIds;

  // The teams this person can pick from / filter by — everything unless
  // they're scoped to a subset.
  const myTeams = useMemo(() => (myTeamIds ? teams.filter((t) => myTeamIds.has(t.id)) : teams), [teams, myTeamIds]);

  // Whether a lead/player/group falls inside the signed-in user's scope. A
  // lead assigned to them always stays visible, whatever its team.
  const inMyTeams = (record) => {
    if (!myTeamIds) return true;
    if (record?.assignedTo && record.assignedTo === user?.uid) return true;
    return !!record?.teamId && myTeamIds.has(record.teamId);
  };

  const addTeam = (name) => addDoc(collection(db, 'academies', academyId, 'teams'), {
    name: name.trim().replace(/\s+/g, ' '),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const renameTeam = (teamId, name) => updateDoc(doc(db, 'academies', academyId, 'teams', teamId), {
    name: name.trim().replace(/\s+/g, ' '),
    updatedAt: serverTimestamp(),
  });
  const deleteTeam = (teamId) => deleteDoc(doc(db, 'academies', academyId, 'teams', teamId));

  return { teams, teamsById, hasTeams, loading, myTeamIds, myTeams, isScoped, inMyTeams, addTeam, renameTeam, deleteTeam };
}

// Value for a team <select>/filter: '' = all teams, NO_TEAM = records with
// no team set, otherwise a team id.
export const NO_TEAM = '__none__';

export function matchesTeamFilter(record, filter) {
  if (!filter) return true;
  if (filter === NO_TEAM) return !record?.teamId;
  return record?.teamId === filter;
}

// playerId → teamId for every player at the academy — for lists of records
// that only point at a player (invoices) to be filtered/scoped by team.
// Only subscribes when `enabled` (i.e. the academy actually has teams).
export function usePlayerTeamMap(enabled) {
  const { academyId } = useAuth();
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled || !academyId) { setMap({}); setLoading(false); return undefined; }
    setLoading(true);
    return subscribeWithRetry(query(collection(db, 'players'), where('academyId', '==', academyId)), (snap) => {
      setMap(Object.fromEntries(snap.docs.map((d) => [d.id, d.data().teamId || null])));
      setLoading(false);
    }, { onGiveUp: () => setLoading(false) });
  }, [enabled, academyId]);

  return { playerTeams: map, loading };
}
